import { MemoryObjectStore, type ObjectStore } from "@foreman/app-core";

/** IndexedDB-backed ObjectStore (structured clone, so it can hold a non-extractable CryptoKey). */
function indexedDbObjectStore(dbName: string): ObjectStore {
  let opened: Promise<IDBDatabase> | null = null;
  const open = () =>
    (opened ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(dbName, 1);
      req.onupgradeneeded = () => req.result.createObjectStore("kv");
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error("IndexedDB is blocked"));
    }));
  const run = async <T,>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const req = fn(db.transaction("kv", mode).objectStore("kv"));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  };
  return {
    get: (key) => run("readonly", (s) => s.get(key)),
    set: async (key, value) => void (await run("readwrite", (s) => s.put(value, key))),
    delete: async (key) => void (await run("readwrite", (s) => s.delete(key))),
  };
}

/**
 * Opens browser storage for secrets. If IndexedDB is unavailable (some private modes), falls back to
 * memory: the phone identity then lasts only until the tab closes, and `persistent` is false so the
 * app can say so.
 */
export async function openObjectStore(dbName = "foreman"): Promise<{ store: ObjectStore; persistent: boolean }> {
  try {
    const store = indexedDbObjectStore(dbName);
    await store.get("probe");
    return { store, persistent: true };
  } catch {
    return { store: new MemoryObjectStore(), persistent: false };
  }
}
